import Dropdown, {Anchor} from '@jetbrains/ring-ui/components/dropdown/dropdown'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import sortBy from 'lodash/sortBy'
import {memo, useCallback, useMemo} from 'react'

import EntityPath from '../../../../containers/EntityPath'
import {getBuildsArg} from '../../../../rest/builds'
import {getOverviewHref} from '../../../../routes'
import {getBuild, getBuildsReady, makeGetBuilds} from '../../../../selectors'
import {restApi} from '../../../../services/rest'
import type {BuildId} from '../../../../types'
import BuildNumberContainer from '../../BuildNumber/BuildNumber.container'
import Link from '../../Link/Link'
import Popup from '../../Popup/Popup.lazy'
import RouterLinkWrapper from '../../RouterLinkWrapper/RouterLinkWrapper'

import {getDependantsLocator} from './BuildPartOf.utils'

import styles from './BuildPartOf.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  buildId: BuildId
}

function BuildPath({buildId}: Props) {
  const buildTypeId = useAppSelector(state => getBuild(state, buildId)?.buildType)
  return (
    <div className={styles.buildPath}>
      <EntityPath
        buildTypeId={buildTypeId}
        projectOrBuildTypeNode={{nodeType: 'all'}}
        withLeafStatusIcon={false}
      />
      <span className={styles.separator}>{'\u00a0/\u00a0'}</span>
      <BuildNumberContainer className={styles.buildNumber} buildId={buildId} hideStar />
    </div>
  )
}

function BuildPartOf({buildId}: Props) {
  const locator = getDependantsLocator(buildId)
  const getBuilds = useMemo(makeGetBuilds, [])
  const buildsCount = useAppSelector(state => getBuilds(state, locator).length)
  const {buildsResult} = restApi.endpoints.getAllBuildsNormalized.useQuery(
    getBuildsArg(locator, {
      withRunningInfo: true,
      withQueuedInfo: true,
      withBuildTypeDetails: true,
    }),
    {selectFromResult: ({data}) => ({buildsResult: data})},
  )
  const buildIds = useMemo(() => {
    const {result, entities} = buildsResult ?? {}
    return sortBy(result, id => entities?.builds?.[id]?.buildType)
  }, [buildsResult])
  const isReady = useAppSelector(state => getBuildsReady(state, locator))

  const chainLink = `${getOverviewHref({buildId})}?buildTab=dependencies&mode=chain`

  const renderAnchor = useMemo(
    () => <Anchor className={styles.anchor}>{`${buildsCount} builds`}</Anchor>,
    [buildsCount],
  )

  const renderLinkToChain = useCallback(
    (text: string) => (
      <RouterLinkWrapper>
        <Link className={styles.chainLink} href={chainLink}>
          {text}
        </Link>
      </RouterLinkWrapper>
    ),
    [chainLink],
  )

  const firstBuildId = buildIds[0]
  const renderSingleLink = useMemo(() => <BuildPath buildId={firstBuildId} />, [firstBuildId])
  const renderDropdown = useMemo(
    () => (
      <Dropdown anchor={renderAnchor} hoverShowTimeOut={300} hoverHideTimeOut={300} hoverMode>
        <Popup>
          <div className={styles.popupContent}>
            {[...buildIds].map(id => (
              <BuildPath key={id} buildId={id} />
            ))}
          </div>
        </Popup>
      </Dropdown>
    ),
    [buildIds, renderAnchor],
  )

  return buildsCount > 0 ? (
    <tr>
      <th>{'Part of'}</th>
      {isReady ? (
        <td>
          <div className={styles.wrapper}>
            {buildsCount === 1 ? renderSingleLink : renderDropdown}
            {renderLinkToChain('Chain')}
          </div>
        </td>
      ) : (
        <td>
          <LoaderInline>{'Loading...'}</LoaderInline>
        </td>
      )}
    </tr>
  ) : null
}

export default memo(BuildPartOf)
